<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { Loader } from '@googlemaps/js-api-loader'
import AppLink from '@/Shared/Components/Links/AppLink.vue'
import { haversineDistance } from '@/Shared/Helpers/mapHelper.js'
import debounce from 'lodash/debounce'
import { GoogleMap, CustomMarker, Polyline } from 'vue3-google-map'
import BusMapRouteTable from './BusMapRouteTable.vue'
import SelectInput from '@/Shared/Components/Forms/SelectInput.vue'
import ImportantInformation from '@/Shared/Components/Sections/ImportantInformation.vue'
import PrimaryButton from '@/Shared/Components/Buttons/PrimaryButton.vue'
import AutoComplete from '@/Public/Partials/ExploreBusOption/AutoComplete.vue'
import AppSpinner from '@/Shared/Components/AppSpinner.vue'
import { showReportDialog } from '@sentry/vue'

const props = defineProps({
  data: [Object, Array],
  title: String,
})

const loader = new Loader({
  apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
  version: 'weekly',
  libraries: ['geometry', 'places'],
})

const daysInWeek = [
  { id: 'monday', name: 'Monday' },
  { id: 'tuesday', name: 'Tuesday' },
  { id: 'wednesday', name: 'Wednesday' },
  { id: 'thursday', name: 'Thursday' },
  { id: 'friday', name: 'Friday' },
  { id: 'saturday', name: 'Saturday' },
  { id: 'sunday', name: 'Sunday' },
]

const strokeColor = ref([
  { stroke: '#7F009F', argb: '0x7F009Fff', routeId: '' },
  { stroke: '#003AE7', argb: '0x003AE7ff', routeId: '' },
  { stroke: '#098A45', argb: '0x098A45ff', routeId: '' },
  { stroke: '#C5A205', argb: '0xC5A205ff', routeId: '' },
  { stroke: '#C2002A', argb: '0xC2002Aff', routeId: '' },
])

const day = ref('monday')
const map = ref(null)
const loading = ref(false)
const apiPromise = loader.load()
const autocomplete = ref(null)
//neded on first load
const routes = ref(props.data.routes)
const center = ref(props.data.location)
const searchableCenter = ref(null)
const showPolylines = ref(props.data.show_route_ids)
const decodedPolylines = ref([])
const mapBounds = ref(null)
const routeSearch = ref(false)
const zoom = ref(11)
const distance = ref(null)

const kentBounds = ref({
  north: 52.55617,
  south: 49.82631,
  west: -1.05012,
  east: 2.661,
})

const getPolylines = async () => {
  apiPromise.then((google) => {
    mapBounds.value = new google.maps.LatLngBounds()
    routes.value.forEach((route) => {
      const polylines = route.polyline
      polylines.forEach((polyline) => {
        const decodedPolyline = google.maps.geometry.encoding.decodePath(polyline)

        // Extend bounds for the decoded polyline
        if (props.data.type == 'route' && props.data.show_route_ids.includes(route.id)) {
          decodedPolyline.forEach((point) => {
            mapBounds.value.extend(point)
          })
        }
        // Add route-specific metadata to the polyline
        decodedPolyline.routeId = route.id
        decodedPolyline.show = route.show_polyline
        decodedPolyline.name = route.short_name
        // Add the decoded polyline to the collection
        decodedPolylines.value.push(decodedPolyline)
      })
    })
  })
  showPolylines.value.forEach((routeId) => {
    assignEmptyStroke(routeId)
  })
  loading.value = false
}

const onCenterChange = debounce(() => {
  searchableCenter.value = map.value.map.getCenter()
  searchableCenter.value = { lat: searchableCenter.value.lat(), lng: searchableCenter.value.lng() }
  if (searchableCenter.value) {
    distance.value = haversineDistance(
      center.value.lat,
      center.value.lng,
      searchableCenter.value.lat,
      searchableCenter.value.lng,
    )
    //if the users move center more then 6km from prev one we will allow another search
    if (distance.value > 6) {
      routeSearch.value = true
    } else {
      routeSearch.value = false
    }
  }
}, 500)

watch(
  () => map.value?.ready,
  (ready) => {
    if (props.data.type !== 'route') return
    if (!ready) return
    map.value.map.fitBounds(mapBounds.value)
  },
)

const assignEmptyStroke = (id) => {
  const emptySlot = strokeColor.value.find((item) => item.routeId === '')
  if (emptySlot) {
    emptySlot.routeId = id
  }
}

const handleCheckboxChange = ({ id, checked }) => {
  if (checked) {
    //assign empty stroke on the go
    if (!strokeColor.value.find((item) => item.routeId === id)) {
      assignEmptyStroke(id)
    }

    // Add to showPolylines if not already included
    if (!showPolylines.value.includes(id)) {
      showPolylines.value.push(id)
    }
  } else {
    // Remove the route ID when unchecked
    showPolylines.value = showPolylines.value.filter((routeId) => routeId !== id)

    // Optional: Reset strokeColor to '' for removed route
    const slotToClear = strokeColor.value.find((item) => item.routeId === id)
    if (slotToClear) {
      slotToClear.routeId = ''
    }
  }
}

onMounted(() => {
  getPolylines()
})

const reloadMap = async (lat, lng) => {
  try {
    loading.value = true
    const response = await axios.get(
      route('bus.map.update', {
        lat: lat,
        lng: lng,
        day: day.value,
        show_route_ids: showPolylines.value ? showPolylines.value : [],
      }),
    )
    const updatedData = response.data
    resetAllRouteIds()
    showPolylines.value = []
    showPolylines.value = updatedData.show_route_ids
    routes.value = updatedData.routes
    routeSearch.value = false
    getPolylines()
  } catch (error) {
    console.error('Failed to reload map data:', error)
  }
}

const visiblePolylines = computed(() => {
  return decodedPolylines.value.filter((polyline) => showPolylines.value.includes(polyline.routeId))
})

const downloadMap = () => {
  axios
    .post(
      route('bus.map.download'),
      {
        routes: showPolylines.value,
        day: day.value,
        route_colors: strokeColor.value,
        boundaries: map.value.map.getBounds(),
      },
      {
        responseType: 'blob',
      },
    )
    .then((response) => {
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const blobUrl = window.URL.createObjectURL(blob)

      // Get current date in YYYY-MM-DD format
      const currentDate = new Date().toISOString().split('T')[0]
      const fileName = `KCCMap-${currentDate}.pdf`

      // Create an anchor element and trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName // Set the dynamic filename
      link.click()

      // Clean up the Blob URL
      window.URL.revokeObjectURL(blobUrl)
    })
    .catch((error) => {
      console.error('Error downloading map:', error)
    })
}

const searchServices = () => {
  reloadMap(searchableCenter.value.lat, searchableCenter.value.lng)
  //assiging new center to searchable center
  center.value = searchableCenter.value
  distance.value = 0
  routeSearch.value = false
}

const getColor = (routeId) => {
  // Find the object in the array that matches the routeId
  const color = strokeColor.value.find((item) => item.routeId === routeId)

  // Log and return the stroke if found, otherwise return null or a default color
  return color ? color.stroke : null
}

const resetAllRouteIds = () => {
  strokeColor.value.forEach((item) => {
    item.routeId = ''
  })
}

const params = {
  lat: null,
  lng: null,
  type: null,
  day: null,
  routeId: null,
}

const onSearch = () => {
  params.type = autocomplete.value.type
  params.day = 'monday'

  if (params.type != 'route') {
    params.lat = autocomplete.value.lat
    params.lng = autocomplete.value.lng
  } else {
    params.routeId = autocomplete.value.id
  }

  window.location.href = route('bus.map.show', params)
}
</script>

<template>
  <p class="mt-10 text-2xl font-bold">Create a map</p>
  <div class="md:flex justify-between">
    <p class="mt-2 lg:w-1/2">
      Select the service from the list below that you want to see on your map. You can download and print this map. The
      downloaded map will contain a key.
    </p>
    <PrimaryButton
      class="mt-3 md:mt-0 w-full md:w-auto justify-center"
      as="button"
      @click="downloadMap"
    >
      Download Map
    </PrimaryButton>
  </div>

  <GoogleMap
    ref="map"
    :center="center"
    :api-promise="apiPromise"
    :zoom="zoom"
    class="w-full h-[500px] my-4"
    :bounds="mapBounds"
    :restriction="{
      latLngBounds: kentBounds,
      strictBounds: true,
    }"
    :minZoom="10"
    :styles="[
      {
        featureType: 'transit.station.bus',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ]"
    @center_changed="onCenterChange"
  >
    <div
      v-for="(decodedPolyline, index) in visiblePolylines"
      :key="index"
    >
      <Polyline
        :options="{
          path: decodedPolyline,
          geodesic: true,
          strokeColor: getColor(decodedPolyline.routeId),
          strokeOpacity: 1.0,
          strokeWeight: 4,
          zIndex: 1,
        }"
      />

      <CustomMarker
        :options="{
          position: { lat: parseFloat(decodedPolyline[5].lat()), lng: parseFloat(decodedPolyline[5].lng()) },
        }"
      >
        <p
          class="font-bold px-2 text-white"
          :style="{ backgroundColor: getColor(decodedPolyline.routeId) }"
        >
          {{ decodedPolyline.name }}
        </p>
      </CustomMarker>
      <p v-if="decodedPolylines.length === 0">Loading polylines...</p>
    </div>
  </GoogleMap>
  <div class="md:flex md:justify-between mt-10">
    <div>
      <p class="font-bold">Services in this area</p>
    </div>
    <div class="flex items-center mt-2 md:mt-0">
      <p class="mr-3 font-bold">Day</p>
      <SelectInput
        id="day"
        v-model="day"
        class="w-[180px]"
        placeholder="Monday"
        @update:model-value="reloadMap(center.lat, center.lng)"
        value-key="id"
        :items="daysInWeek"
      >
        <template #selected="{ selected }">
          <span class="mx-5 block truncate">
            {{ selected ? selected.name : '' }}
          </span>
        </template>
        <template #default="{ item, selected }">
          <span :class="[selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate']">
            {{ item.name }}
          </span>
        </template>
      </SelectInput>
    </div>
  </div>
  <div v-if="!routeSearch">
    <ImportantInformation class="mt-4 md:mt-0 md:w-3/4">
      Please note that these are our standard routes within Kent. Specific times and bus stops may differ on certain
      dates such as bank holidays and festive occasions. For specific dates please use the
      <AppLink :href="route('timetable.cities.index')">timetables page</AppLink>
    </ImportantInformation>

    <div v-if="loading">
      <div class="flex p-10">
        <AppSpinner
          class="w-8 h-8 mt-6 mx-auto"
          fg-color="stroke-primary-blue"
          bg-color="stroke-gray-100"
        />
      </div>
    </div>

    <div v-else>
      <BusMapRouteTable
        v-if="routes.length > 0"
        :checked="showPolylines"
        :routes="routes"
        class="mt-10"
        @update="handleCheckboxChange"
      />
      <div v-else>
        <div class="mt-8">
          <p class="text-2xl font-bold">No services found in this area</p>
          <p class="mt-8">Your search returned no results</p>
          <p class="mt-9">Adjust the map position to search again</p>
          <div class="md:flex py-4">
            <div class="flex outline-none md:w-3/4">
              <div class="flex items-center grow md:mb-4 lg:mb-0 mr-4">
                <div class="relative w-full">
                  <AutoComplete v-model="autocomplete" />
                </div>
              </div>
              <div class="flex">
                <div class="hidden md:block">
                  <PrimaryButton
                    class="w-full px-12 justify-center"
                    @click="onSearch"
                  >
                    Search
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="mt-10"
  >
    <div class="p-12 grid justify-items-center bg-[#F6F6F6]">
      <PrimaryButton @click="searchServices">Search services in this area</PrimaryButton>
    </div>
  </div>
</template>
